import { computed, getCurrentInstance, Ref } from 'vue';
import CatalogV2Service from '@/api/services/catalog-v2.service';
import { useMenuStore } from '@/composition/use-menu';
import {
  availabilitySnapshot,
  IAvailabilityChoice,
  IAvailabilityContext,
  invertAvailabilityOp,
  isEmptyAvailabilityOp,
  toggleAvailability,
} from '@/domain/catalog/availability';
import type { ICatalogAccess } from '@/domain/catalog/catalog-access';
import type { TMenuV2EventLabel } from '@/domain/catalog/catalog-analytics';
import { AlertType } from '@/interfaces/IAlert';
import {
  IAvailabilityOp,
  IBulkAvailabilityChange,
} from '@/interfaces/ICatalogV2';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { ICatalogPageState } from './catalog-page-state';
import type { ICatalogData } from './use-catalog-data';
import type { IBulkOutcome, ICatalogWrites } from './use-catalog-writes';

export interface ICatalogAvailabilityDeps {
  state: ICatalogPageState;
  data: ICatalogData;
  access: Ref<ICatalogAccess>;
  writes: ICatalogWrites;
}

const NO_OUTCOME: IBulkOutcome = { appliedIds: [] };

/**
 * Availability on the Menú page (plan §5.9): the product and category
 * switches, the «¿Dónde…?» chooser of multi-branch stores, and bulk
 * «Ocultar / Mostrar», each with «Deshacer» (the inverse op per product,
 * deltas only, so another device's branch changes survive the undo).
 */
export function useCatalogAvailability(deps: ICatalogAvailabilityDeps) {
  const { state, data, access, writes } = deps;
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  /** Rows whose switch write is in flight: a double tap sends nothing more. */
  const pendingItems = new Set<number>();
  const pendingCategories = new Set<number>();

  const availabilityContext = computed<IAvailabilityContext>(() => ({
    branchId: data.branchId.value,
    multiBranch: data.activeStoreBranches.value.length > 1,
    // Inactive branches too: showing «solo aquí» keeps it hidden there.
    allBranchIds: data.storeBranches.value.map((branch) => branch.id),
    canHideEverywhere: access.value.canHideEverywhere,
  }));

  function branchName(): string {
    return restaurantStore.selectedBranch?.name ?? '';
  }

  /** Shown in this branch only (the rest of the chain unchanged or hidden). */
  function showsHere(op: IAvailabilityOp): boolean {
    if (!availabilityContext.value.multiBranch) return false;
    if (op.hiddenBranchIds !== undefined)
      return !!op.hiddenBranchIds && op.hiddenBranchIds.length > 0;
    return (op.showInBranchIds?.length ?? 0) > 0;
  }

  /** The toast of one product's or category's change. */
  function availabilityMessage(
    entity: 'item' | 'category',
    name: string,
    op: IAvailabilityOp
  ): string {
    // `toast.shown` / `toast.categoryShown`, …
    const key = (change: string) =>
      entity === 'item'
        ? `menuPage.toast.${change}`
        : `menuPage.toast.category${change[0].toUpperCase()}${change.slice(1)}`;
    const params = { name, branch: branchName() };
    const showing =
      op.visible === true || (op.showInBranchIds?.length ?? 0) > 0;
    if (showing) return t(key(showsHere(op) ? 'shownHere' : 'shown'), params);
    if (op.hideInBranchIds?.length) return t(key('hiddenHere'), params);
    return availabilityContext.value.multiBranch
      ? t(key('hiddenEverywhere'), params)
      : t(key('hidden'), params);
  }

  /**
   * One op on several products. `message` receives the products as saved
   * (bulk «Mostrar» counts those still hidden elsewhere).
   */
  async function applyItemsAvailability(
    items: readonly IItem[],
    op: IAvailabilityOp,
    message: (saved: IItem[]) => string,
    label: TMenuV2EventLabel
  ): Promise<IBulkOutcome> {
    const rid = writes.restaurantId();
    const targets = items.filter((item) => !pendingItems.has(item.id));
    if (!rid || !targets.length) return NO_OUTCOME;
    const changes: IBulkAvailabilityChange[] = targets.map((item) => ({
      itemId: item.id,
      op,
    }));
    for (const item of targets) pendingItems.add(item.id);
    try {
      const written = await writes.writeInChunks(
        changes,
        (part) => CatalogV2Service.bulkAvailability(rid, part),
        (result) => result.before.map((before) => before.itemId)
      );
      if (!written || rid !== writes.restaurantId()) return NO_OUTCOME;
      const saved = written.results.flatMap((result) => result.items);
      menuStore.upsertItems(saved, { own: true });
      writes.pin(saved.map((item) => item.id));
      writes.track(label);

      const inverses: IBulkAvailabilityChange[] = written.results
        .flatMap((result) => result.before)
        .map((before) => ({
          itemId: before.itemId,
          op: invertAvailabilityOp(before, op),
        }))
        .filter((change) => !isEmptyAvailabilityOp(change.op));
      writes.showResult(
        writes.bulkToast(targets.length, written, () => message(saved)),
        inverses.length
          ? async () => {
              let skipped = 0;
              await writes.undoInChunks(
                inverses,
                (part) => CatalogV2Service.bulkAvailability(rid, part),
                (result) => {
                  skipped += result.skippedIds.length;
                  if (rid === writes.restaurantId())
                    menuStore.upsertItems(result.items, { own: true });
                }
              );
              return { kept: 0, skipped };
            }
          : null
      );
      return { appliedIds: written.appliedIds };
    } finally {
      for (const item of targets) pendingItems.delete(item.id);
    }
  }

  async function applyCategoryAvailability(
    category: ICategory,
    op: IAvailabilityOp
  ): Promise<boolean> {
    const rid = writes.restaurantId();
    if (!rid || pendingCategories.has(category.id)) return false;
    const before = availabilitySnapshot(category);
    pendingCategories.add(category.id);
    try {
      const result = await writes.guarded(() =>
        CatalogV2Service.patchCategory(category.id, { availability: op })
      );
      if (!result || rid !== writes.restaurantId()) return false;
      menuStore.upsertCategories([result.category], { own: true });
      writes.track('toggle');
      const inverse = invertAvailabilityOp(before, op);
      writes.showResult(
        {
          message: availabilityMessage('category', category.name, op),
          type: AlertType.Success,
        },
        isEmptyAvailabilityOp(inverse)
          ? null
          : async () => {
              const undone = await CatalogV2Service.patchCategory(category.id, {
                availability: inverse,
              });
              if (rid === writes.restaurantId())
                menuStore.upsertCategories([undone.category], { own: true });
              return { kept: 0, skipped: 0 };
            }
      );
      return true;
    } finally {
      pendingCategories.delete(category.id);
    }
  }

  function applyItemAvailability(item: IItem, op: IAvailabilityOp) {
    void applyItemsAvailability(
      [item],
      op,
      () => availabilityMessage('item', item.name, op),
      'toggle'
    );
  }

  /** The product switch: one tap, or the «¿Dónde…?» chooser. */
  function toggleItem(item: IItem, on: boolean, anchor: HTMLElement | null) {
    if (pendingItems.has(item.id)) return;
    const action = toggleAvailability(item, on, availabilityContext.value);
    if (action.kind === 'blocked') return;
    if (action.kind === 'choose')
      state.panel = {
        kind: 'availability',
        entity: 'item',
        targetId: item.id,
        anchor,
        turnOn: action.turnOn,
        choices: action.choices,
      };
    else if (!isEmptyAvailabilityOp(action.op))
      applyItemAvailability(item, action.op);
  }

  /** The category switch. */
  function toggleCategory(
    category: ICategory,
    on: boolean,
    anchor: HTMLElement | null
  ) {
    if (pendingCategories.has(category.id)) return;
    const action = toggleAvailability(category, on, availabilityContext.value);
    if (action.kind === 'blocked') return;
    if (action.kind === 'choose')
      state.panel = {
        kind: 'availability',
        entity: 'category',
        targetId: category.id,
        anchor,
        turnOn: action.turnOn,
        choices: action.choices,
      };
    else if (!isEmptyAvailabilityOp(action.op))
      void applyCategoryAvailability(category, action.op);
  }

  /** A button of the «¿Dónde…?» chooser. */
  function chooseAvailability(choice: IAvailabilityChoice) {
    const panel = state.panel;
    state.panel = null;
    if (panel?.kind !== 'availability') return;
    if (panel.entity === 'category') {
      const category = data.categoryById.value.get(panel.targetId);
      if (category) void applyCategoryAvailability(category, choice.op);
      return;
    }
    const item = data.itemById.value[panel.targetId];
    if (item) applyItemAvailability(item, choice.op);
  }

  /** «{n} productos ocultos / visibles» (+ those still hidden elsewhere). */
  function bulkVisibilityMessage(visible: boolean, saved: IItem[]): string {
    const n = saved.length;
    const base = tc(
      visible ? 'menuPage.toast.bulkShown' : 'menuPage.toast.bulkHidden',
      n,
      { n }
    );
    if (!visible) return base;
    const otherActive = new Set(
      data.activeStoreBranches.value
        .map((branch) => branch.id)
        .filter((id) => id !== data.branchId.value)
    );
    const stillHidden = saved.filter(
      (item) =>
        !item.visible ||
        !!item.hiddenBranchesIds?.some((id) => otherActive.has(Number(id)))
    ).length;
    return stillHidden
      ? `${base} ${tc('menuPage.toast.stillHiddenElsewhere', stillHidden, {
          k: stillHidden,
        })}`
      : base;
  }

  /** Bulk «Ocultar» with a scope op (the selection bar's chooser). */
  function hideItems(items: readonly IItem[], op: IAvailabilityOp) {
    const onlyHere = op.visible !== false && !!op.hideInBranchIds?.length;
    return applyItemsAvailability(
      items,
      op,
      (saved) =>
        onlyHere
          ? tc('menuPage.toast.bulkHiddenHere', saved.length, {
              n: saved.length,
              branch: branchName(),
            })
          : bulkVisibilityMessage(false, saved),
      'bulk_hide'
    );
  }

  /**
   * Bulk «Mostrar»: visible and shown here; roles without every branch only
   * clear this branch.
   */
  function showItems(items: readonly IItem[]): Promise<IBulkOutcome> {
    const branchId = data.branchId.value;
    const op: IAvailabilityOp = access.value.canHideEverywhere
      ? { visible: true }
      : {};
    if (branchId !== null) op.showInBranchIds = [branchId];
    if (isEmptyAvailabilityOp(op)) return Promise.resolve(NO_OUTCOME);
    return applyItemsAvailability(
      items,
      op,
      (saved) => bulkVisibilityMessage(true, saved),
      'bulk_show'
    );
  }

  /** Category ⋯ «Ocultar / Mostrar todos sus productos» (every branch). */
  function setCategoryProductsVisible(category: ICategory, visible: boolean) {
    const items = data.items.value.filter(
      (item) => item.categoryId === category.id && item.visible !== visible
    );
    return applyItemsAvailability(
      items,
      { visible },
      (saved) => bulkVisibilityMessage(visible, saved),
      visible ? 'bulk_show' : 'bulk_hide'
    );
  }

  /** Why this role cannot turn on a product or category hidden everywhere. */
  function toggleBlockedTitle(
    target: IItem | ICategory,
    entity: 'item' | 'category' = 'item'
  ): string | null {
    if (!access.value.canToggle || target.visible) return null;
    const action = toggleAvailability(target, true, availabilityContext.value);
    if (action.kind !== 'blocked') return null;
    return t(
      entity === 'item'
        ? 'menuPage.availability.needsAllBranches'
        : 'menuPage.availability.needsAllBranchesCategory'
    );
  }

  return {
    availabilityContext,
    toggleItem,
    toggleCategory,
    chooseAvailability,
    hideItems,
    showItems,
    setCategoryProductsVisible,
    toggleBlockedTitle,
  };
}

export type ICatalogAvailability = ReturnType<typeof useCatalogAvailability>;
